import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {
  addUser,
  getMeta,
  getBulkAccessList,
  getProjectsByRoleAndEntity,
  getRoles,
  getSecurityGroupsByRoleAndProject,
  deleteUser,
  getUsers,
  removeUser,
  getNotLoggedInUsers,
  getBulkUserAccess,
  getBulkSecurityGroups,
  getBulkUserRights,
  saveBulkAccess,
  getUserById,
  editUser,
  getUserRole,
  resendLink,
  getDomainsByRoleAndEntity,
  changeUserRole,
  getSecurityGroupsByProject,
  getHeadersProjectsList,
} from '../../api/endpoints/usersApi';
import {
  BulkAccessListRequest,
  IGetRolesResponse,
  IGetUsersRequestPayload,
  IGetUsersResponse,
  IDeleteUserRequestPayload,
  IRemoveUserArgs,
  IRemoveUserResponse,
  IGetNotLoggedInUsersPayload,
  IGetNotLoggedInUsersResponse,
  IGetMetaResponse,
  IAddUserSuccessResponse,
  IAddUserPayload,
  IBulkUserAccessResponse,
  IGetBulkUserRightsPayload,
  IResendLinkResponse,
  IResendLinkRequestPayload,
  SecurityGroupItem,
  IEditUserRequestPayload,
  IEditUserSuccessResponse,
  UseDomainsOptions,
} from '../../api/types/user.type';
import {ISecurityGroup} from '../../shared/types';

export const useUsers = (
  payload: IGetUsersRequestPayload,
  scope?: {roleId?: number; entityId?: number; projectId?: number}
) => {
  return useQuery<IGetUsersResponse, Error>({
    queryKey: ['users', payload, scope],
    queryFn: () => getUsers(payload),
    retry: 2,
  });
};

export const useRoles = () => {
  return useQuery<IGetRolesResponse, Error>({
    queryKey: ['roles'],
    queryFn: () => getRoles(),
    retry: 2,
  });
};

export const useBulkAccessList = () => {
  return useMutation({
    mutationFn: (payload: BulkAccessListRequest) => getBulkAccessList(payload),
  });
};

export const useDeleteUser = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<IRemoveUserResponse, Error, IDeleteUserRequestPayload>({
    mutationFn: deleteUser,
    mutationKey: ['deleteUser'],
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useRemoveUser = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<IRemoveUserResponse, Error, IRemoveUserArgs>({
    mutationFn: removeUser,
    mutationKey: ['removeUser'],
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};
export const useUserMeta = () => {
  return useQuery<IGetMetaResponse, Error>({
    queryKey: ['userMeta'],
    queryFn: () => getMeta(),
    retry: 2,
  });
};

export const useAddUser = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<IAddUserSuccessResponse, Error, IAddUserPayload>({
    mutationFn: addUser,
    mutationKey: ['addUser'],
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useNotYetLoggedInUsers = (
  payload: IGetNotLoggedInUsersPayload
) => {
  return useQuery<IGetNotLoggedInUsersResponse, Error>({
    queryKey: ['notLoggedInUsers', payload],
    queryFn: () => getNotLoggedInUsers(payload),
    retry: 2,
  });
};

export const useBulkUserAccess = (enabled: boolean = true) =>
  useQuery<IBulkUserAccessResponse>({
    queryKey: ['bulkUserAccess'],
    queryFn: getBulkUserAccess,
    enabled,
  });

export const useBulkSecurityGroups = (
  projectId?: number | null,
  enabled = false
) =>
  useQuery<ISecurityGroup>({
    queryKey: ['bulkSecurityGroups', projectId],
    queryFn: () => getBulkSecurityGroups(projectId!),
    enabled: enabled && !!projectId,
  });

export const useBulkUserRights = (
  bulkRoleID: string | number | null,
  bulkProjectID?: number,
  bulkSecurityGroupID?: number,
  enabled: boolean = false
) =>
  useQuery({
    queryKey: [
      'bulkUserRights',
      bulkRoleID,
      bulkProjectID,
      bulkSecurityGroupID,
    ],
    queryFn: () => {
      if (!bulkRoleID) throw new Error('bulkRoleID is required');

      return getBulkUserRights({
        bulkRoleID,
        ...(bulkProjectID !== undefined && {bulkProjectID}),
        ...(bulkSecurityGroupID !== undefined && {bulkSecurityGroupID}),
        userId: 0,
        roleId: 0,
        projectId: 0,
        securityGroupId: 0,
        canManage: false,
        canIngestions: false,
        canProductions: false,
        canSearching: false,
        canReceiveTeamCenterMessages: false,
        canConceptExplorer: false,
        canCommunicationsExplorer: false,
        canAnalyticsPanels: false,
        canEditCustomMetadata: false,
        canViewFullDocList: false,
        canAllReports: false,
        canReportAdministration: false,
        canDownloadNative: false,
        canImageRedaction: false,
        canNativeRedaction: false,
        canAudioRedaction: false,
        canHighlighting: false,
        canReviewerRemarks: false,
        canUndockPanels: false,
        accessRight: false,
        selectedUserID: [],
        canAIModels: false,
        canPredictiveCoding: false,
      });
    },
    enabled: !!bulkRoleID && enabled,
  });
export const useSaveBulkAccess = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: IGetBulkUserRightsPayload) => saveBulkAccess(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['bulkUserRights']});
    },
  });
};

export const useUserRole = () => {
  return useQuery<IGetRolesResponse, Error>({
    queryKey: ['userRole'],
    queryFn: getUserRole,
    retry: 2,
  });
};

export const useUser = (
  userId: number,
  projectId?: number,
  entityId?: number
) => {
  return useQuery({
    queryKey: ['user', userId, projectId, entityId],
    queryFn: () => getUserById(userId, projectId, entityId),
    retry: 2,
  });
};

export const useEditUser = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<
    IEditUserSuccessResponse,
    Error,
    {userId: number | string; payload: IEditUserRequestPayload}
  >({
    mutationFn: editUser,
    mutationKey: ['editUser'],
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useResendLink = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<IResendLinkResponse, Error, IResendLinkRequestPayload>({
    mutationFn: resendLink,
    mutationKey: ['resendLink'],
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useChangeUserRole = () => {
  return useMutation({
    mutationFn: changeUserRole,
  });
};

export const useProjects = (
  roleID: number,
  entityID: number,
  enabled: boolean
) =>
  useQuery({
    queryKey: ['projects', roleID, entityID],
    queryFn: () => getProjectsByRoleAndEntity(roleID, entityID),
    enabled: Boolean(enabled && roleID !== -1 && entityID !== -1),
  });

export const useHeadersProjectsList = (entityID: number) =>
  useQuery({
    queryKey: ['headersProjectsList', entityID],
    queryFn: () => getHeadersProjectsList(entityID),
  });

export const useSecurityGroups = (
  roleId: number,
  projectId: number,
  enabled: boolean
) => {
  return useQuery<SecurityGroupItem[], Error>({
    queryKey: ['securityGroups', roleId, projectId],
    queryFn: () => getSecurityGroupsByRoleAndProject(roleId, projectId),
    enabled: Boolean(enabled && roleId !== -1 && projectId !== -1),
  });
};

export const useDomains = (
  roleId: number,
  entityId: number,
  options?: UseDomainsOptions
) => {
  return useQuery({
    queryKey: ['domains', roleId, entityId],
    queryFn: () => getDomainsByRoleAndEntity(roleId, entityId),
    enabled: Boolean(options?.enabled ?? (roleId !== -1 && entityId !== -1)),
  });
};

export const useProjectsRoleImpersonation = (
  roleId: number,
  entityId: number,
  options?: {enabled?: boolean}
) => {
  return useQuery({
    queryKey: ['projects', roleId, entityId],
    queryFn: () => getProjectsByRoleAndEntity(roleId, entityId),
    enabled: options?.enabled ?? roleId !== -1,
  });
};

export const useSecurityGroupsRoleImpersonation = (
  roleId: number,
  projectId: number,
  options?: {enabled?: boolean}
) => {
  return useQuery({
    queryKey: ['securityGroups', roleId, projectId],
    queryFn: () => getSecurityGroupsByRoleAndProject(roleId, projectId),
    enabled: options?.enabled ?? (roleId !== -1 && projectId !== -1),
  });
};

export const useProjectSecurityGroups = (
  projectId: number,
  enabled: boolean
) => {
  return useQuery<SecurityGroupItem[], Error>({
    queryKey: ['projectSecurityGroups', projectId],
    queryFn: () => getSecurityGroupsByProject(projectId),
    enabled: Boolean(enabled && projectId !== -1),
  });
};
